import {
    AfterContentInit,
    Component,
    ContentChildren,
    forwardRef,
    Input,
    QueryList,
} from "@angular/core"
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from "@angular/forms"
import { UserProfileCheckboxComponent } from "./user-profile-checkbox/user-profile-checkbox.component"
import { SubjectEnum, SUBJECT_OTHER_PREFIX } from "../../models/constants"

@Component({
    selector: "app-user-profile-checkbox-group",
    templateUrl: "./user-profile-checkbox-group.component.html",
    styleUrls: ["./user-profile-checkbox-group.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: UserProfileCheckboxGroupComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: forwardRef(() => UserProfileCheckboxGroupComponent),
        },
    ],
    standalone: false,
})
export class UserProfileCheckboxGroupComponent
    implements ControlValueAccessor, Validator, AfterContentInit
{
    @ContentChildren(UserProfileCheckboxComponent)
    contentChildren!: QueryList<UserProfileCheckboxComponent>

    @Input() gtmClass: string = ""
    @Input() allowOther: boolean = false
    readonly otherPrefix = SUBJECT_OTHER_PREFIX
    value = []
    isDisabled = false
    onValidatorChange: () => void = () => {}
    onChange = (value: string[]) => {}
    onTouch = () => {}

    ngAfterContentInit(): void {
        this.contentChildren.forEach((el: UserProfileCheckboxComponent) => {
            el.isDisabled = this.isDisabled
            el.gtmClass = this.gtmClass
        })
    }

    writeValue(value: string[]): void {
        this.value = value
        this.onValidatorChange()
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // A bare "Other: " (Other selected but no text entered) is not a real selection.
        const selectedValues = this.allowOther
            ? (this.value || []).filter((v) => v !== this.otherPrefix)
            : this.value || []
        return selectedValues.length <= 0
            ? {
                  error: {
                      message: "Please select at least one option!",
                  },
              }
            : null
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled

        if (!this.contentChildren) return
        isDisabled
            ? this.contentChildren.forEach(
                  (el: UserProfileCheckboxComponent) => (el.isDisabled = true)
              )
            : this.contentChildren.forEach(
                  (el: UserProfileCheckboxComponent) => (el.isDisabled = false)
              )
    }

    toggleValue(selectedValue: any): void {
        // if selected value is N/A, deselect all the other checkboxes
        if (selectedValue === "N/A") {
            let naValueFound = this.value.find((el) => el === selectedValue)
            if (naValueFound) {
                this.value = []
            } else {
                this.value = [selectedValue]
            }

            this.onTouch()
            this.onChange(this.value)

            return
        }

        // toggle checkbox logic, uncheck the item if is found in list, else add it
        let itemFound = this.value.find((el) => el === selectedValue)
        if (itemFound) {
            this.value = this.value.filter((el) => el !== itemFound)
        } else {
            // if N/A checkbox is already selected, uncheck N/A
            let naValue = this.value.find((el) => el === SubjectEnum.NA)
            if (naValue) this.value = []

            this.value = [...this.value, selectedValue]

            // If "Other" is selected but its free text is empty, choosing another option clears it.
            if (this.allowOther && this.isOtherSelected() && !this.otherText.trim()) {
                this.value = this.value.filter((el) => !this.isOtherEntry(el))
            }
        }
        this.onChange(this.value)
    }

    isSelected(valueToCheck: string): boolean {
        return (this.value || []).includes(valueToCheck)
    }

    // ----- "Other" free-text option (only active when allowOther is true) -----

    private isOtherEntry(entry: string): boolean {
        return typeof entry === "string" && entry.startsWith(this.otherPrefix)
    }

    private get otherEntry(): string | undefined {
        return (this.value || []).find((el) => this.isOtherEntry(el))
    }

    isOtherSelected(): boolean {
        return this.allowOther && this.otherEntry !== undefined
    }

    get otherText(): string {
        const entry = this.otherEntry
        return entry ? entry.slice(this.otherPrefix.length) : ""
    }

    toggleOther(): void {
        if (this.isOtherSelected()) {
            // uncheck Other -> remove the entry (its input disappears)
            this.value = (this.value || []).filter((el) => !this.isOtherEntry(el))
        } else {
            // selecting Other clears N/A, then adds an empty Other entry so the input appears
            const withoutNa = (this.value || []).filter((el) => el !== SubjectEnum.NA)
            this.value = [...withoutNa, this.otherPrefix]
        }
        this.onTouch()
        this.onChange(this.value)
    }

    setOtherText(text: string): void {
        // strip commas so a single Other value can't break the comma-delimited storage
        const clean = (text || "").replace(/,/g, "")
        const withoutOther = (this.value || []).filter((el) => !this.isOtherEntry(el))
        this.value = [...withoutOther, this.otherPrefix + clean]
        this.onChange(this.value)
    }
}
